'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Question {
  id: number;
  question: string;
  options: string[];
}

interface QuestionnaireModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (recommendation: string) => void;
}

const QuestionnaireModal = ({ isOpen, onClose, onComplete }: QuestionnaireModalProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const questions: Question[] = [
    {
      id: 1,
      question: 'What is your primary fitness goal?',
      options: ['Improve Speed & Agility', 'Build Strength', 'General Fitness & Wellness', 'Athletic Performance'],
    },
    {
      id: 2,
      question: 'What is your age group?',
      options: ['6-12 years', '13-17 years', '18-54 years', '55+ years'],
    },
    {
      id: 3,
      question: 'What is your current fitness level?',
      options: ['Beginner (New to fitness)', 'Intermediate (Regular exercise)', 'Advanced (Competitive athlete)'],
    },
    {
      id: 4,
      question: 'How many days per week can you commit to training?',
      options: ['2-3 days', '4-5 days', '6+ days'],
    },
  ];

  const handleAnswer = (answer: string) => {
    setAnswers({ ...answers, [currentStep]: answer });
    
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      const recommendation = getRecommendation({ ...answers, [currentStep]: answer });
      onComplete(recommendation);
      resetQuestionnaire();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const resetQuestionnaire = () => {
    setCurrentStep(0);
    setAnswers({});
  };

  const handleClose = () => {
    resetQuestionnaire();
    onClose();
  };

  const getRecommendation = (allAnswers: Record<number, string>): string => {
    const goal = allAnswers[0];
    const age = allAnswers[1];

    if (age === '6-12 years' || age === '13-17 years') {
      return 'Kids Foundation Program';
    }
    if (age === '55+ years') {
      return 'Senior Fitness Initiative';
    }
    if (goal === 'Improve Speed & Agility') {
      return 'Speed & Athletic Training';
    }
    if (goal === 'Build Strength') {
      return 'Strength & Conditioning';
    }
    return 'General Fitness Program';
  };

  if (!isOpen) return null;

  const progress = ((currentStep + 1) / questions.length) * 100;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-modal w-full max-w-2xl">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h2 className="text-xl font-heading font-bold text-primary">Find Your Perfect Program</h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-muted rounded-full transition-colors duration-300"
            aria-label="Close questionnaire"
          >
            <Icon name="XMarkIcon" size={24} variant="outline" className="text-text-primary" />
          </button>
        </div>

        <div className="px-6 py-2">
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-accent h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-text-secondary mt-2 text-right">
            Question {currentStep + 1} of {questions.length}
          </p>
        </div>

        <div className="p-6">
          <h3 className="text-lg font-heading font-bold text-primary mb-6">
            {questions[currentStep].question}
          </h3>

          <div className="space-y-3">
            {questions[currentStep].options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswer(option)}
                className="w-full px-6 py-4 bg-muted hover:bg-accent hover:text-accent-foreground text-left rounded-lg transition-all duration-300 text-text-primary font-heading font-semibold text-sm group"
              >
                <div className="flex items-center justify-between">
                  <span>{option}</span>
                  <Icon
                    name="ArrowRightIcon"
                    size={20}
                    variant="outline"
                    className="opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  />
                </div>
              </button>
            ))}
          </div>

          {currentStep > 0 && (
            <button
              onClick={handleBack}
              className="mt-6 px-4 py-2 text-accent hover:text-accent/80 font-heading font-semibold text-sm transition-colors duration-300 flex items-center space-x-2"
            >
              <Icon name="ArrowLeftIcon" size={16} variant="outline" />
              <span>Previous Question</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionnaireModal;